// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2 rejects.

import { FormattedMessage } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { AgentRow } from "./AgentRow";
import { useMemo } from "react";
import { agentPath, displayAgent } from "./agents";
import { useAgentsByIds } from "./use-agent-search";
import type { MatchAgent } from "./design-codec";

/**
 * Result-step surface: the agents this project runs with, shown so the outcome reads with the full
 * context. Explicitly labelled as context — the roster above it stays persons-only.
 */
export function SelectedAgentsPanel({ agents }: { agents: MatchAgent[] }) {
  // Only the stored agents, never the instance's whole list.
  const ids = useMemo(() => agents.map((a) => a.id), [agents]);
  const { byId } = useAgentsByIds(ids);
  if (agents.length === 0) return null;
  return (
    <div className="box bg-surface p-5">
      <BoxHeader title={<FormattedMessage id="match.result.agentsTitle" defaultMessage="Agenten" />} />
      <p className="mb-4 max-w-xl text-smycles-meta text-ink-muted">
        <FormattedMessage
          id="match.result.agentsIntro"
          defaultMessage="Mit diesen KI-Agenten arbeitet das Projekt. Sie sind Kontext und kein Teammitglied: das Team oben besteht ausschließlich aus Personen."
        />
      </p>
      <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2">
        {agents.map((a) => (
          <AgentRow
            key={a.id}
            {...displayAgent(a, byId.get(a.id))}
            // Undefined for an agent whose genesis row is gone: the row still renders from the
            // stored snapshot, but there is no page, so it must not offer one.
            to={byId.has(a.id) ? agentPath(a.id) : undefined}
          />
        ))}
      </div>
    </div>
  );
}
